import { useEffect, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { useToast } from '../contexts/ToastContext';
import api from '../services/api';

interface PermissionDeniedDetail {
  method: string;
  path: string;
  message: string;
}

const DEDUPE_WINDOW_MS = 30_000;

const PermissionDeniedToast: React.FC = () => {
  const { showToast } = useToast();
  const { accounts } = useMsal();
  const recentRef = useRef<Map<string, number>>(new Map());

  const userEmail = accounts[0]?.username || 'unknown';

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<PermissionDeniedDetail>).detail;
      if (!detail) return;

      const { method, path, message } = detail;

      // Belt-and-suspenders: never react to a 403 on the logging endpoint itself.
      if (path.startsWith('/permission-denied-log')) return;

      const key = `${method} ${path}`;
      const now = Date.now();
      const last = recentRef.current.get(key);
      if (last && now - last < DEDUPE_WINDOW_MS) return;
      recentRef.current.set(key, now);

      const pageUrl = window.location.href;
      // User-facing toast disabled — was interrupting users too often.
      // Telemetry below still runs so admins can review gaps in PermissionErrors.
      // To re-enable, uncomment the showToast block.
      // const diagnostic = [
      //   `Permission denied: ${message}`,
      //   `METHOD: ${method}`,
      //   `PATH: ${path}`,
      //   `PAGE: ${pageUrl}`,
      //   `USER: ${userEmail}`,
      //   `TIME: ${new Date(now).toISOString()}`,
      // ].join('\n');
      //
      // showToast({
      //   type: 'error',
      //   message: `Permission denied: ${method} ${path}`,
      //   persistent: true,
      //   copyPayload: diagnostic,
      // });

      // Fire-and-forget telemetry so admins can find permission gaps
      // without waiting for users to report them.
      api.post('/permission-denied-log', {
        method,
        path,
        page_url: pageUrl,
        message,
      }).catch(() => { /* swallow — telemetry must not surface errors */ });
    };

    window.addEventListener('permission-denied', handler as EventListener);
    return () => window.removeEventListener('permission-denied', handler as EventListener);
  }, [showToast, userEmail]);

  return null;
};

export default PermissionDeniedToast;
